import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { REHYDRATE } from "redux-persist";

import Datastore from "../../communication/Datastore";
import { StartMonitor, StopMonitor } from "../../sockets/statusManager";
import store, { IAppState } from "../../state/Store";
import { initialiseClientSystems } from "../../state/core/ClientSystem";
import UserActivity from "../../types/admin/UserActivity";
import UserActivityLogin from "../../types/admin/UserActivityLogin";
import ClientSystem from "../../types/core/ClientSystem";
import UserDigest from "../../types/core/UserDigest";
import School from "../../types/core/School";
import AdminImport from "../../types/admin/AdminImport";
import { getDigestDetails } from "../core/User";
import { initialiseSchoolDetails } from "../core/School";

// Define the type for our state store entry
interface AdminActivityState {
    activity: UserActivity[];
    activityLogins: UserActivityLogin;
    publishing: AdminImport[];
    status: string;
    publishStatus: string;
    error: string;
}

// Define the initial state for our state store entry
const initialState: AdminActivityState = {
    activity: [],
    activityLogins: null,
    publishing: [],
    status: "idle",
    publishStatus: "idle",
    error: null,
};

interface AdminActivityWatchPayload {
    activity: UserActivity[];
    activityLogins: UserActivityLogin;
}

// Create a slice to hold all our reducers
const adminActivitySlice = createSlice({
    name: "adminactivity",
    initialState: initialState,
    reducers: {
        setActivity: (state, action: PayloadAction<UserActivity[]>) => {
            state.activity = action.payload;
        },
        setActivityLogins: (
            state,
            action: PayloadAction<UserActivityLogin>,
        ) => {
            state.activityLogins = action.payload;
        },
        setPublishing: (state, action: PayloadAction<AdminImport[]>) => {
            state.publishing = action.payload.map(
                item => new AdminImport(item),
            );
        },
    },
    extraReducers(builder) {
        builder
            .addCase(REHYDRATE, (_, rawData: any) => {
                if (rawData?.key !== "tts") return undefined;
                return {
                    activity: [],
                    activityLogins: null,
                    publishing: [],
                    status: "idle",
                    publishStatus: "idle",
                    error: null,
                };
            })
            .addCase(fetchAdminActivity.pending, (state, action) => {
                state.status = "loading";
            })
            .addCase(fetchAdminActivity.fulfilled, (state, action) => {
                state.status = "succeeded";
                state.activity = processActivityData(
                    action.payload.activity,
                    action.payload.schools,
                    action.payload.clientSystems,
                    action.payload.users,
                );
                state.activityLogins = action.payload.logins;
            })
            .addCase(fetchAdminActivity.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message;
            })
            .addCase(fetchAdminPublishing.pending, (state, action) => {
                state.publishStatus = "loading";
            })
            .addCase(fetchAdminPublishing.fulfilled, (state, action) => {
                state.publishStatus = "succeeded";
                state.publishing = action.payload.map(
                    item => new AdminImport(item),
                );
            })
            .addCase(fetchAdminPublishing.rejected, (state, action) => {
                state.publishStatus = "failed";
                state.error = action.error.message;
            });
    },
});

const processActivityData = (
    activity: UserActivity[],
    schools: School[],
    clientSystems: ClientSystem[],
    users: UserDigest[],
) => {
    return activity.reduce((prev: UserActivity[], current: UserActivity) => {
        let existing = prev.find(item => item.userID === current.userID);

        if (existing) {
            current.systems.forEach(system => {
                if (
                    !existing.systems.find(
                        item => item.systemID === system.systemID,
                    )
                ) {
                    system.clientSystem = clientSystems.find(
                        item => item.id === system.systemID,
                    );
                    existing.systems.push(system);
                }
            });
        } else {
            current.school = schools.find(item => item.id === current.schoolID);
            current.user = users.find(item => item.id === current.userID);
            current.systems.forEach(
                system =>
                    (system.clientSystem = clientSystems.find(
                        item => item.id === system.systemID,
                    )),
            );
            prev.push(new UserActivity(current));
        }

        return prev;
    }, [] as UserActivity[]);
};

// This is a thunk, they are used to load the state
const fetchAdminActivity = createAsyncThunk(
    "adminactivity/fetchAdminActivity",
    async (_, thunkAPI) => {
        const response = await Promise.all([
            Datastore.getType<UserActivity[]>("api/Users/Activity/Admin"),
            Datastore.getType<UserActivityLogin>(
                "api/Users/Activity/Logins/Admin",
            ),
        ]);

        const additions = await Promise.all([
            initialiseSchoolDetails(),
            initialiseClientSystems(),
            thunkAPI.dispatch(
                getDigestDetails(response[0].map(item => item.userID)),
            ),
        ]);

        const state = store.getState() as IAppState;

        return {
            schools: state.db.school.schools,
            clientSystems: state.db.clientSystem.clientSystems,
            users: additions[2].payload as UserDigest[],
            activity: response[0],
            logins: response[1],
        };
    },
);
export const initialiseAdminActivity = createAsyncThunk(
    "adminactivity/initialiseAdminActivity",
    async (_, thunkAPI) => {
        let adminActivity = store.getState().db.adminActivity;

        if (adminActivity.status === "idle")
            thunkAPI.dispatch(fetchAdminActivity());
    },
);
const fetchAdminPublishing = createAsyncThunk(
    "adminactivity/fetchAdminPublishing",
    async (_, thunkAPI) => {
        return await Datastore.getType<AdminImport[]>(
            "api/SystemIO/ActivitySummary/Publishing",
        );
    },
);
export const initialiseAdminPublishing = createAsyncThunk(
    "adminactivity/initialiseAdminPublishing",
    async (_, thunkAPI) => {
        let adminActivity = store.getState().db.adminActivity;

        if (adminActivity.publishStatus === "idle")
            thunkAPI.dispatch(fetchAdminPublishing());
    },
);
export const reinitialiseAdminPublishing = createAsyncThunk(
    "adminactivity/reinitialiseAdminPublishing",
    async (_, thunkAPI) => {
        thunkAPI.dispatch(fetchAdminPublishing());
    },
);

const { setActivity, setActivityLogins, setPublishing } =
    adminActivitySlice.actions;

const watchActivityType: string = "adminactivitystatus";
let watchingActivity: boolean = false;

export function WatchAdminActivity() {
    if (!watchingActivity) {
        watchingActivity = true;
        StartMonitor(
            watchActivityType,
            1,
            2000,
            false,
            async (data: AdminActivityWatchPayload) => {
                const { school, clientSystem } = store.getState().db;

                const users = await store.dispatch(
                    getDigestDetails(data.activity.map(item => item.userID)),
                );

                store.dispatch(
                    setActivity(
                        processActivityData(
                            data.activity,
                            school.schools,
                            clientSystem.clientSystems,
                            users.payload as UserDigest[],
                        ),
                    ),
                );
                store.dispatch(setActivityLogins(data.activityLogins));
            },
            () => (watchingActivity = false),
        );
    }
}

export function StopWatchAdminActivity() {
    if (watchingActivity) {
        watchingActivity = false;
        StopMonitor(watchActivityType, 1);
    }
}

const watchPublishingType: string = "adminimportstatus";
let watchingPublishing: boolean = false;

export function WatchAdminPublishing() {
    if (!watchingPublishing) {
        watchingPublishing = true;
        StartMonitor(
            watchPublishingType,
            1,
            2000,
            false,
            (data: AdminImport[]) => {
                store.dispatch(setPublishing(data));
            },
            () => watchingPublishing = false);
    }
}

export function StopWatchAdminPublishing() {
    if (watchingPublishing) {
        watchingPublishing = false;
        StopMonitor(
            watchPublishingType,
            1);
    }
}

export default adminActivitySlice.reducer;
